import { lazy, type ComponentType } from "react";
import type { StoryMeta, IllustrationProps } from "~/types/story";

interface StoryEntry extends StoryMeta {
  component: () => Promise<{ default: ComponentType }>;
  background?: ComponentType<IllustrationProps>;
}

// Lazy-loaded story components
const stories: StoryEntry[] = [
  // === 9-12 Age Group: Historical Figures (migrated from tamWrittenStories) ===
  {
    id: "harriet-tubman",
    title: "Harriet's Brave Journeys",
    slug: "harriet-tubman",
    ageGroup: "9-12",
    category: "historical-figures",
    description: "The extraordinary story of Harriet Tubman's courage and the Underground Railroad",
    readingTime: 8,
    moral: "Courage and faith can light the way through the darkest of times",
    tags: ["harriet-tubman", "underground-railroad", "civil-war", "freedom", "courage"],
    component: () => import("./9-12/historical-figures/harriet-tubman"),
  },
  {
    id: "hedy-lamarr",
    title: "Beyond Beauty: The Brilliant Mind of Hedy Lamarr",
    slug: "hedy-lamarr",
    ageGroup: "9-12",
    category: "historical-figures",
    description: "How a Hollywood star secretly invented the technology behind WiFi",
    readingTime: 10,
    moral: "True genius knows no limits — be curious and never stop learning",
    tags: ["hedy-lamarr", "invention", "wifi", "hollywood", "science"],
    component: () => import("./9-12/historical-figures/hedy-lamarr"),
  },
  {
    id: "nelson-mandela",
    title: "The Unbreakable Spirit: The Story of Nelson Mandela",
    slug: "nelson-mandela",
    ageGroup: "9-12",
    category: "historical-figures",
    description: "The inspiring journey of Nelson Mandela from prisoner to president",
    readingTime: 8,
    moral: "Forgiveness is the most powerful choice you can make",
    tags: ["nelson-mandela", "south-africa", "apartheid", "forgiveness", "leadership"],
    component: () => import("./9-12/historical-figures/nelson-mandela"),
  },
  {
    id: "mozart",
    title: "Mozart: The Boy Who Filled the World with Music",
    slug: "mozart",
    ageGroup: "9-12",
    category: "arts-music",
    description: "The tale of a child prodigy whose music still enchants the world",
    readingTime: 8,
    moral: "Even the most dazzling talents grow brighter with practice and kindness",
    tags: ["mozart", "music", "classical", "prodigy", "vienna"],
    component: () => import("./9-12/arts-music/mozart"),
  },
  {
    id: "mayflower",
    title: "The Voyage of the Mayflower",
    slug: "mayflower",
    ageGroup: "9-12",
    category: "world-history",
    description: "A story of courage and community on the journey to the New World",
    readingTime: 8,
    moral: "Be brave when facing challenges and always show kindness",
    tags: ["mayflower", "pilgrims", "thanksgiving", "voyage", "community"],
    component: () => import("./9-12/world-history/mayflower"),
  },

  // === 6-8 Age Group: Interactive Fairy Tales ===
  {
    id: "light-bulb",
    title: "The Light That Changed the Night",
    slug: "light-bulb",
    ageGroup: "6-8",
    category: "inventions-technology",
    description: "Click to place light bulbs and discover how Edison and Swan lit up the world",
    readingTime: 6,
    moral: "The greatest magic comes from many minds working together",
    tags: ["light-bulb", "edison", "swan", "electricity", "interactive"],
    component: () => import("~/pages/fairytale/inventions/LightBulb"),
  },
  {
    id: "astrolabe",
    title: "The Secret of the Moving Stars",
    slug: "astrolabe",
    ageGroup: "6-8",
    category: "inventions-technology",
    description: "Explore the ancient astrolabe and learn how it unlocked the secrets of the stars",
    readingTime: 7,
    moral: "Curiosity and mentorship can unlock the mysteries of the universe",
    tags: ["astrolabe", "stars", "navigation", "astronomy", "interactive"],
    component: () => import("~/pages/fairytale/inventions/Astrolabe"),
  },
  {
    id: "morse-code",
    title: "The Magic of Morse Code",
    slug: "morse-code",
    ageGroup: "6-8",
    category: "inventions-technology",
    description: "Tap dots and dashes to unlock the story of Samuel Morse and the telegraph",
    readingTime: 5,
    moral: "Dreams and hard work can make the impossible come true",
    tags: ["morse-code", "telegraph", "samuel-morse", "communication", "interactive"],
    component: () => import("~/pages/fairytale/inventions/MorseCode"),
  },
  {
    id: "northern-star",
    title: "The Tale of Little Dipper and Big Dipper",
    slug: "northern-star",
    ageGroup: "6-8",
    category: "space-astronomy",
    description: "Find the constellations and learn how every star has its purpose",
    readingTime: 7,
    moral: "Every star has its place — together we shine brighter",
    tags: ["constellations", "polaris", "big-dipper", "little-dipper", "interactive"],
    component: () => import("~/pages/fairytale/astronomy/NorthernStar"),
  },

  // === 6-8 Age Group: History Stories ===
  {
    id: "medieval-london",
    title: "Life in Medieval London",
    slug: "medieval-london",
    ageGroup: "6-8",
    category: "world-history",
    description: "Step into the bustling streets of medieval London",
    readingTime: 3,
    moral: "History helps us understand how people lived before us",
    tags: ["medieval", "london", "history", "daily-life"],
    component: () => import("~/components/stories/medieval-london"),
  },
  {
    id: "norman-conquest",
    title: "The Norman Conquest",
    slug: "norman-conquest",
    ageGroup: "6-8",
    category: "world-history",
    description: "How William of Normandy changed England forever in 1066",
    readingTime: 3,
    moral: "Great events can reshape the course of history",
    tags: ["norman-conquest", "1066", "william", "england"],
    component: () => import("~/components/stories/norman-conquest"),
  },
  {
    id: "printing-press",
    title: "The Printing Press Revolution",
    slug: "printing-press",
    ageGroup: "6-8",
    category: "inventions-technology",
    description: "How Gutenberg changed the spread of knowledge forever",
    readingTime: 3,
    moral: "Knowledge shared is knowledge multiplied",
    tags: ["gutenberg", "printing-press", "books", "knowledge"],
    component: () => import("~/components/stories/printing-press"),
  },
  {
    id: "steam-engine",
    title: "The Steam Engine",
    slug: "steam-engine",
    ageGroup: "6-8",
    category: "inventions-technology",
    description: "The invention that powered the Industrial Revolution",
    readingTime: 3,
    moral: "Innovation transforms how we live and work",
    tags: ["steam-engine", "industrial-revolution", "invention"],
    component: () => import("~/components/stories/steam-engine"),
  },
];

export default stories;

// Helper functions
export function getStoriesByAgeGroup(ageGroup: string) {
  return stories.filter((s) => s.ageGroup === ageGroup);
}

export function getStoriesByCategory(category: string) {
  return stories.filter((s) => s.category === category);
}

export function getStoryBySlug(slug: string) {
  return stories.find((s) => s.slug === slug);
}

export function getStoryById(id: string) {
  return stories.find((s) => s.id === id);
}

export function lazyLoadStory(story: StoryEntry) {
  return lazy(story.component);
}
